import React, { useState } from 'react';
import { Calculator, Building2, DollarSign, Truck, Users, Wrench } from 'lucide-react';

interface CostCategory {
  name: string;
  items: { description: string; cost: number }[];
}

const CostEstimatorPage: React.FC = () => {
  const [propertySize, setPropertySize] = useState('');
  const [locationType, setLocationType] = useState('urban');
  const [businessType, setBusinessType] = useState('retail');
  
  const [costs, setCosts] = useState<CostCategory[]>([
    {
      name: 'Initial Costs',
      items: [
        { description: 'Security Deposit', cost: 0 },
        { description: 'First Month Rent', cost: 0 },
        { description: 'Insurance Down Payment', cost: 0 },
      ]
    },
    {
      name: 'Renovation Costs',
      items: [
        { description: 'Interior Improvements', cost: 0 },
        { description: 'Exterior Improvements', cost: 0 },
        { description: 'Permits and Licenses', cost: 0 },
      ]
    },
    {
      name: 'Equipment Costs',
      items: [
        { description: 'Furniture and Fixtures', cost: 0 },
        { description: 'Technology Setup', cost: 0 },
        { description: 'Security System', cost: 0 },
      ]
    }
  ]);

  const calculateCosts = () => {
    const size = parseFloat(propertySize) || 0;
    const baseRate = locationType === 'urban' ? 150 : 100;
    const businessMultiplier = {
      retail: 1.2,
      restaurant: 1.5,
      office: 1.0,
      warehouse: 0.8
    }[businessType] || 1.0;

    const newCosts = costs.map(category => ({
      ...category,
      items: category.items.map(item => {
        let cost = 0;
        switch (item.description) {
          case 'Security Deposit':
            cost = size * baseRate * 2;
            break;
          case 'First Month Rent':
            cost = size * baseRate;
            break;
          case 'Insurance Down Payment':
            cost = size * 5;
            break;
          case 'Interior Improvements':
            cost = size * 75 * businessMultiplier;
            break;
          case 'Exterior Improvements':
            cost = size * 25;
            break;
          case 'Permits and Licenses':
            cost = 2500 * businessMultiplier;
            break;
          case 'Furniture and Fixtures':
            cost = size * 50 * businessMultiplier;
            break;
          case 'Technology Setup':
            cost = size * 15;
            break;
          case 'Security System':
            cost = 3000 + (size * 2);
            break;
          default:
            cost = 0;
        }
        return { ...item, cost };
      })
    }));

    setCosts(newCosts);
  };

  const totalCost = costs.reduce((total, category) => 
    total + category.items.reduce((sum, item) => sum + item.cost, 0), 0
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-2">
          <Calculator className="h-8 w-8" />
          Cost Estimator
        </h1>
        <p className="text-muted-foreground">
          Calculate the estimated costs for your business location
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-card rounded-lg p-6 shadow-lg">
          <h2 className="text-2xl font-semibold mb-6">Property Details</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Property Size (sq ft)
              </label>
              <input
                type="number"
                value={propertySize}
                onChange={(e) => setPropertySize(e.target.value)}
                className="w-full p-2 rounded border bg-background"
                placeholder="Enter property size"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Location Type
              </label>
              <select
                value={locationType}
                onChange={(e) => setLocationType(e.target.value)}
                className="w-full p-2 rounded border bg-background"
              >
                <option value="urban">Urban</option>
                <option value="suburban">Suburban</option>
                <option value="rural">Rural</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Business Type
              </label>
              <select
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                className="w-full p-2 rounded border bg-background"
              >
                <option value="retail">Retail</option>
                <option value="restaurant">Restaurant</option>
                <option value="office">Office</option>
                <option value="warehouse">Warehouse</option>
              </select>
            </div>

            <button
              onClick={calculateCosts}
              className="w-full bg-primary text-primary-foreground py-2 rounded hover:bg-primary/90 transition"
            >
              Calculate Costs
            </button>
          </div>
        </div>

        <div className="bg-card rounded-lg p-6 shadow-lg">
          <h2 className="text-2xl font-semibold mb-6">Cost Breakdown</h2>
          
          <div className="space-y-6">
            {costs.map((category, index) => (
              <div key={index} className="space-y-2">
                <h3 className="font-medium text-lg">{category.name}</h3>
                {category.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex justify-between text-sm">
                    <span>{item.description}</span>
                    <span>${item.cost.toLocaleString()}</span>
                  </div>
                ))}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-medium">
                    <span>Subtotal</span>
                    <span>
                      ${category.items.reduce((sum, item) => sum + item.cost, 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between text-xl font-bold">
                <span>Total Estimated Cost</span>
                <span>${totalCost.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card p-4 rounded-lg border border-border">
          <Building2 className="h-6 w-6 mb-2 text-primary" />
          <h3 className="font-medium">Property Costs</h3>
          <p className="text-sm text-muted-foreground">
            Includes deposits, rent, and basic setup costs
          </p>
        </div>
        
        <div className="bg-card p-4 rounded-lg border border-border">
          <Wrench className="h-6 w-6 mb-2 text-primary" />
          <h3 className="font-medium">Renovation Costs</h3>
          <p className="text-sm text-muted-foreground">
            Improvements, permits, and compliance costs
          </p>
        </div>
        
        <div className="bg-card p-4 rounded-lg border border-border">
          <Truck className="h-6 w-6 mb-2 text-primary" />
          <h3 className="font-medium">Equipment Costs</h3>
          <p className="text-sm text-muted-foreground">
            Furniture, technology, and security setup
          </p>
        </div>
      </div>
    </div>
  );
};

export default CostEstimatorPage;